import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import axiosInstance from "../../../utils/networkWrapper";
import { isValidEmail } from "../../../utils/phoneAndEmailUtils";

/** A `SystemDropdown` item (`DropdownItem<string>`-compatible). `secondaryLabel` is the email, set only
 * when `label` is a first name — the dropdown's search matches both, so typing either finds the user. */
export interface ActivityLogPerformedByOption {
	label: string;
	value: string;
	secondaryLabel?: string;
	/** What a selected chip shows: the first name plus the email, so two people who share a first name
	 * don't read as the same chip. Set only alongside `secondaryLabel`. */
	selectedLabel?: string;
}

/** Mirrors `ActivityLogPerformedByOption` (`backend/models/team.py`). */
interface ActivityLogPerformedByOptionResponse {
	value: string;
	firstName: string | null;
}

// A new key (the v2 response has a different shape), so a v1 `string[]` still sitting in the cache
// can never be read back as v2 objects.
const ACTIVITY_LOG_PERFORMED_BY_OPTIONS_QUERY_KEY = "activity-log-performed-by-options-v2";
const PERFORMED_BY_OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * The two masked sentinel values `mask_activity_log_username` (`backend/services/activity_log.py`)
 * can substitute for a real username on the wire — given a real translated label instead of the raw
 * literal, same convention as `whatLabel`/`channelLabel`.
 */
const SUPPORT_BUCKET = "support";
const SYSTEM_BUCKET = "system";
const PERFORMED_BY_SENTINEL_LABEL_KEY: Record<string, string> = {
	[SUPPORT_BUCKET]: "activityLog.grouped.performedBy.support",
	[SYSTEM_BUCKET]: "activityLog.grouped.performedBy.system",
};
const INTERNAL_EMAIL_DOMAIN = "@inhouse.health";
/** Local parts the backend's `is_valid_email` (`backend/database/users.py`) rejects as not a real email. */
const PLACEHOLDER_EMAIL_LOCAL_PARTS: ReadonlySet<string> = new Set(["unknown", "placeholder"]);

const isRealEmail = (value: string): boolean => isValidEmail(value) && !PLACEHOLDER_EMAIL_LOCAL_PARTS.has(value.split("@")[0].toLowerCase());

/**
 * The bucket a non-support viewer sees a username as — the frontend copy of
 * `mask_activity_log_username` (`backend/services/activity_log.py`): an InHouse email is "support",
 * anything that isn't a real email (`unknown@…`/`placeholder@…` included) is "system". A bucket value
 * is already masked and returned as-is ("support" is not an email, so masking it again would turn it
 * into "system").
 */
export const maskPerformedByValue = (value: string): string => {
	if (value === SUPPORT_BUCKET || value === SYSTEM_BUCKET) return value;
	if (value.endsWith(INTERNAL_EMAIL_DOMAIN)) return SUPPORT_BUCKET;
	if (!isRealEmail(value)) return SYSTEM_BUCKET;
	return value;
};

/**
 * One option's display: the user's first name with the email underneath when the backend has one,
 * else the translated bucket label ("support"/"system"), else the raw value. Exported so a selected
 * value that is missing from the fetched list (e.g. from a shared link) renders the same way.
 */
export const toPerformedByOption = (value: string, firstName: string | null, t: TFunction): ActivityLogPerformedByOption => {
	if (firstName) return { value, label: firstName, secondaryLabel: value, selectedLabel: `${firstName} (${value})` };
	const sentinelKey = PERFORMED_BY_SENTINEL_LABEL_KEY[value];
	return { value, label: sentinelKey ? t(sentinelKey) : value };
};

/**
 * A selected value the fetched options don't carry (from a shared link). A support viewer sees it as
 * itself; anyone else sees the bucket it masks to, so a link a support user shared never shows a raw
 * internal username. The value stays as it came — the backend masks it before filtering.
 */
export const toMissingPerformedByOption = (value: string, isSupport: boolean, t: TFunction): ActivityLogPerformedByOption => {
	if (isSupport) return toPerformedByOption(value, null, t);
	const { label } = toPerformedByOption(maskPerformedByValue(value), null, t);
	return { value, label };
};

/**
 * `performed_by` filter options, sourced from `GET /activity-log/departments/{id}/grouped/performed-by-options/v2`
 * — every distinct `changed_user` actually seen in this department's grouped-eligible log (masked +
 * deduped for non-support callers, same treatment the list/detail results already get), each with the
 * user's first name where the backend is allowed to show one. Deliberately NOT
 * `GET /staffing/admin-users`: that route only lists department-configured HeadNurse-role, non-support
 * admins, so it misses support-flagged and automation accounts — the ones doing most of the activity.
 * Selecting a masked sentinel value is sent back verbatim (`performed_by=support`); the list route
 * expands it into the real usernames it represents. The option `value` is always the username, so the
 * list request and the URL keep carrying usernames — names are display only.
 */
export const useActivityLogPerformedByOptionsQuery = (): UseQueryResult<ActivityLogPerformedByOption[], Error> => {
	const { chosenDepartment } = useHospitalData();
	const { t } = useTranslation();

	return useQuery({
		queryKey: [ACTIVITY_LOG_PERFORMED_BY_OPTIONS_QUERY_KEY, chosenDepartment?.department_id],
		queryFn: async () => {
			const { data } = await axiosInstance.get<ActivityLogPerformedByOptionResponse[]>(
				`/activity-log/departments/${chosenDepartment.department_id}/grouped/performed-by-options/v2`,
			);
			return data.map(({ value, firstName }) => toPerformedByOption(value, firstName, t)).sort((a, b) => a.label.localeCompare(b.label));
		},
		staleTime: PERFORMED_BY_OPTIONS_STALE_TIME_MS,
		throwOnError: false,
		enabled: !!chosenDepartment?.department_id,
	});
};
